import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Step2Location = ({ formData, onChange, errors = {} }) => {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState({ states: false, districts: false, taluks: false, villages: false });

  // Fetch states on mount
  useEffect(() => {
    fetchStates();
  }, []);

  // Fetch districts when state changes
  useEffect(() => {
    if (formData.state_id) {
      fetchDistricts(formData.state_id);
    } else {
      setDistricts([]);
      setTaluks([]);
      setVillages([]);
    }
  }, [formData.state_id]);

  // Fetch taluks when district changes
  useEffect(() => {
    if (formData.district_id) {
      fetchTaluks(formData.district_id);
    } else {
      setTaluks([]);
      setVillages([]);
    }
  }, [formData.district_id]);

  // Fetch villages when taluk changes
  useEffect(() => {
    if (formData.taluk_id) {
      fetchVillages(formData.taluk_id);
    } else {
      setVillages([]);
    }
  }, [formData.taluk_id]);

  const fetchStates = async () => {
    setLoading(prev => ({ ...prev, states: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/states`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStates(response.data.states || response.data);
    } catch (error) {
      toast.error('Failed to load states');
      console.error('Error fetching states:', error);
    } finally {
      setLoading(prev => ({ ...prev, states: false }));
    }
  };

  const fetchDistricts = async (stateId) => {
    setLoading(prev => ({ ...prev, districts: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/districts/${stateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDistricts(response.data.districts || response.data);
    } catch (error) {
      toast.error('Failed to load districts');
      console.error('Error fetching districts:', error);
    } finally {
      setLoading(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchTaluks = async (districtId) => {
    setLoading(prev => ({ ...prev, taluks: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/taluks/${districtId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaluks(response.data.taluks || response.data);
    } catch (error) {
      toast.error('Failed to load taluks');
      console.error('Error fetching taluks:', error);
    } finally {
      setLoading(prev => ({ ...prev, taluks: false }));
    }
  };

  const fetchVillages = async (talukId) => {
    setLoading(prev => ({ ...prev, villages: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/villages/${talukId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVillages(response.data.villages || response.data);
    } catch (error) {
      toast.error('Failed to load villages');
      console.error('Error fetching villages:', error);
    } finally {
      setLoading(prev => ({ ...prev, villages: false }));
    }
  };

  const handleStateChange = (value) => {
    // Store both ID for cascading and NAME for backend submission
    onChange('state_id', value);
    const selectedState = states.find(s => s.id === value);
    onChange('residential_state', selectedState?.name || '');
    // Reset dependent fields
    onChange('district_id', '');
    onChange('residential_district', '');
    onChange('taluk_id', '');
    onChange('residential_taluk', '');
    onChange('village_id', '');
    onChange('residential_village', '');
  };

  const handleDistrictChange = (value) => {
    // Store both ID for cascading and NAME for backend submission
    onChange('district_id', value);
    const selectedDistrict = districts.find(d => d.id === value);
    onChange('residential_district', selectedDistrict?.name || '');
    // Reset dependent fields
    onChange('taluk_id', '');
    onChange('residential_taluk', '');
    onChange('village_id', '');
    onChange('residential_village', '');
  };

  const handleTalukChange = (value) => {
    // Store both ID for cascading and NAME for backend submission
    onChange('taluk_id', value);
    const selectedTaluk = taluks.find(t => t.id === value);
    onChange('residential_taluk', selectedTaluk?.name || '');
    // Reset dependent field
    onChange('village_id', '');
    onChange('residential_village', '');
  };

  const handleVillageChange = (value) => {
    // Store both ID for UI and NAME for backend submission
    onChange('village_id', value);
    // Handle both object and string formats
    const selectedVillage = typeof villages[0] === 'string' 
      ? value 
      : villages.find(v => v.id === value);
    onChange('residential_village', selectedVillage?.name || selectedVillage || '');
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Farmer Residential Location</h3>
        <p className="text-sm text-gray-600">
          Enter the farmer's residential address (not land location).
          Select location in hierarchical order: State → District → Taluk → Village
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* State */}
          <div className="space-y-2">
            <Label htmlFor="state_id">
              State <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.state_id || ''}
              onValueChange={handleStateChange}
              disabled={loading.states}
            >
              <SelectTrigger 
                data-testid="state-select"
                className={errors.state_id ? 'border-red-500' : ''}
              >
                <SelectValue placeholder={loading.states ? 'Loading states...' : 'Select state'} />
              </SelectTrigger>
              <SelectContent>
                {states.map((state) => (
                  <SelectItem key={state.id} value={state.id}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state_id && <p className="text-sm text-red-500">{errors.state_id}</p>}
          </div>

          {/* District */}
          <div className="space-y-2">
            <Label htmlFor="district_id">
              District <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.district_id || ''}
              onValueChange={handleDistrictChange}
              disabled={!formData.state_id || loading.districts}
            >
              <SelectTrigger 
                data-testid="district-select"
                className={errors.district_id ? 'border-red-500' : ''}
              >
                <SelectValue 
                  placeholder={
                    !formData.state_id 
                      ? 'Select state first' 
                      : loading.districts 
                      ? 'Loading districts...' 
                      : 'Select district'
                  } 
                />
              </SelectTrigger>
              <SelectContent>
                {districts.map((district) => (
                  <SelectItem key={district.id} value={district.id}>
                    {district.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.district_id && <p className="text-sm text-red-500">{errors.district_id}</p>}
          </div>

          {/* Taluk */}
          <div className="space-y-2">
            <Label htmlFor="taluk_id">
              Taluk <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.taluk_id || ''}
              onValueChange={handleTalukChange}
              disabled={!formData.district_id || loading.taluks}
            >
              <SelectTrigger 
                data-testid="taluk-select"
                className={errors.taluk_id ? 'border-red-500' : ''}
              >
                <SelectValue 
                  placeholder={
                    !formData.district_id 
                      ? 'Select district first' 
                      : loading.taluks 
                      ? 'Loading taluks...' 
                      : 'Select taluk'
                  } 
                />
              </SelectTrigger>
              <SelectContent>
                {taluks.map((taluk) => (
                  <SelectItem key={taluk.id} value={taluk.id}>
                    {taluk.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.taluk_id && <p className="text-sm text-red-500">{errors.taluk_id}</p>}
          </div>

          {/* Village */}
          <div className="space-y-2">
            <Label htmlFor="village_id">
              Village <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.village_id || ''}
              onValueChange={handleVillageChange}
              disabled={!formData.taluk_id || loading.villages}
            >
              <SelectTrigger 
                data-testid="village-select"
                className={errors.village_id ? 'border-red-500' : ''}
              >
                <SelectValue 
                  placeholder={
                    !formData.taluk_id 
                      ? 'Select taluk first' 
                      : loading.villages 
                      ? 'Loading villages...' 
                      : 'Select village'
                  } 
                />
              </SelectTrigger>
              <SelectContent>
                {villages.map((village) => (
                  <SelectItem key={village.id || village} value={village.id || village}>
                    {village.name || village}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.village_id && <p className="text-sm text-red-500">{errors.village_id}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2Location;
